import React from "react";

/* Business profile setup. Guided, not form-heavy. */

// Accepts a bare name ("AskReddit"), a path ("/r/AskReddit"), or a full Reddit
// URL ("https://www.reddit.com/r/AskReddit/"). Returns just the subreddit name
// with original casing — backend dedups and ingests case-insensitively. Kept in
// sync with the same helper in onboarding-shell.jsx / profile-view.jsx.
function parseSubredditName(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const m = s.match(/(?:^|[\/.])r\/([A-Za-z0-9_]{2,21})/i);
  if (m) return m[1];
  return s.replace(/^\/+/, "").split(/[\/?#\s]/)[0];
}

function ProfileSetupScreen({
  initial,
  onSubmit,
  onBack,
  mode = "onboard",
  persistProfile,
  persistGeneratedPlan = true,
}) {
  // mode: "onboard" or "edit"
  const [form, setForm] = React.useState(() => initial || {
    name: "",
    website: "",
    description: "",
    customers: "",
    useCases: [],
    painPoints: [],
    competitors: [],
    keywords: [],
    avoid: [],
    subreddits: [],
  });

  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [apiError, setApiError] = React.useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Verify newly-added subreddits against Reddit (DB-first on the server, which
  // also upserts them into our cache) and DROP any that don't resolve — the same
  // guard the onboarding radar uses, so a mistyped or hallucinated community name
  // never lingers on the profile, since we can't scan a sub that doesn't exist.
  // Names we've already confirmed are remembered so editing the rest of the form
  // doesn't re-hit the API for them.
  const verifiedSubsRef = React.useRef(new Set());
  React.useEffect(() => {
    const pending = (form.subreddits || []).filter((raw) => {
      const key = String(raw || "").replace(/^r\//i, "").trim().toLowerCase();
      return key && !verifiedSubsRef.current.has(key);
    });
    if (!pending.length) return;
    let cancelled = false;
    void (async () => {
      const invalid = new Set();
      await Promise.all(pending.map(async (raw) => {
        const clean = String(raw).replace(/^r\//i, "").trim();
        const key = clean.toLowerCase();
        try {
          const resp = await window.api?.subreddit?.info?.(clean);
          if (resp?.subreddit) verifiedSubsRef.current.add(key);
          else invalid.add(key);
        } catch (e) {
          // 404 / refusal → Reddit can't resolve it → treat as not real.
          invalid.add(key);
        }
      }));
      if (cancelled || !invalid.size) return;
      setForm((f) => ({
        ...f,
        subreddits: (f.subreddits || []).filter(
          (raw) => !invalid.has(String(raw || "").replace(/^r\//i, "").trim().toLowerCase())
        ),
      }));
    })();
    return () => { cancelled = true; };
  }, [form.subreddits]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Product name is required.";
    if (!form.description.trim()) e.description = "A one-line description helps a lot.";
    if (!form.customers.trim()) e.customers = "Describe who your customers are.";
    if (!form.keywords?.length) e.keywords = "Add at least one keyword to monitor.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setApiError("");
    setSubmitting(true);

    let searchPlan = null;
    let persistedProfile = null;
    try {
      if (window.api.auth.isLoggedIn()) {
        // Edit mode (or post-login onboarding) — persist via the API.
        const persist = persistProfile || window.api.profile.save;
        persistedProfile = await persist(form);
        window.api.storage.clearPendingProfile();
      } else {
        // Pre-auth onboarding — hold the profile in temp state. App will
        // flush this to the API on first successful login.
        window.api.storage.writePendingProfile(form);
      }

      // In onboarding mode, generate the search plan now so the next
      // screen ("opportunity map") can render against the user's actual
      // input. Edit mode skips this — the existing plan is preserved
      // until the user explicitly re-runs analysis.
      if (mode === "onboard") {
        const resp = await window.api.profile.expand(form);
        searchPlan = resp.search_plan || null;
        if (searchPlan) {
          // Hold the plan so it survives login/refresh and gets attached
          // to the business_profile when the account is created.
          if (persistGeneratedPlan) {
            window.api.storage.writePendingPlan(searchPlan);
          }
        }
      }
    } catch (e) {
      setSubmitting(false);
      setApiError(e.message || "Could not save profile. Please try again.");
      return;
    }

    setSubmitting(false);
    onSubmit(form, searchPlan, persistedProfile);
  };

  return (
    <div className="screen-enter" style={mode === "edit" ? {} : null}>
      <div className="onboard-panel" style={mode === "edit" ? { maxWidth: "100%" } : null}>
        {mode === "onboard" && (
          <div className="onboard-header">
            <div className="onboard-eyebrow">Step 1 of 2</div>
            <h1 className="onboard-title">Tell us about your product.</h1>
            <p className="onboard-subtitle">
              We use this to infer what to look for. You can refine it after the first scan — most
              founders tune it once and forget it.
            </p>
          </div>
        )}

        <div className="card" style={{ padding: 22 }}>
          <Field
            label="Product name"
            error={errors.name}
            help=""
          >
            <input
              className={"input " + (errors.name ? "has-error" : "")}
              value={form.name}
              onChange={(e) => set("name")(e.target.value)}
              placeholder="GroupCast"
            />
          </Field>

          <Field label="Website" optional>
            <input
              className="input"
              value={form.website}
              onChange={(e) => set("website")(e.target.value)}
              placeholder="groupcast.app"
            />
          </Field>

          <Field
            label="One-line description"
            error={errors.description}
            help="What are you working on?"
          >
            <textarea
              className={"textarea " + (errors.description ? "has-error" : "")}
              value={form.description}
              onChange={(e) => set("description")(e.target.value)}
              placeholder="Chrome extension that helps realtors post content across multiple Facebook groups."
              rows={2}
            />
          </Field>

          <Field
            label="Target customers"
            error={errors.customers}
            help="Who feels the pain hardest? Be specific."
          >
            <textarea
              className={"textarea " + (errors.customers ? "has-error" : "")}
              value={form.customers}
              onChange={(e) => set("customers")(e.target.value)}
              placeholder="Independent realtors and small brokerages doing organic Facebook marketing."
              rows={2}
            />
          </Field>

          <Field
            label="Main use cases"
            help="What jobs does your product get hired to do? Press Enter to add each."
          >
            <TagInput
              values={form.useCases}
              onChange={set("useCases")}
              placeholder="e.g. Cross-posting listings"
            />
          </Field>

          <Field
            label="Pain points your product solves"
            help="What hurts before they have your product?"
          >
            <TagInput
              values={form.painPoints}
              onChange={set("painPoints")}
              placeholder="e.g. Manual copy-paste"
            />
          </Field>

          <Field label="Competitors or alternatives" optional>
            <TagInput
              values={form.competitors}
              onChange={set("competitors")}
              placeholder="e.g. Postermate"
            />
          </Field>

          <div className="divider" />

          <Field
            label="Keywords to include"
            error={errors.keywords}
            help="Phrases that strongly indicate your customer is talking."
          >
            <TagInput
              values={form.keywords}
              onChange={set("keywords")}
              placeholder="e.g. facebook groups"
              accent
            />
          </Field>

          <Field
            label="Keywords to avoid"
            help="Phrases that usually mean the conversation isn't a fit."
          >
            <TagInput
              values={form.avoid}
              onChange={set("avoid")}
              placeholder="e.g. spam"
            />
          </Field>

          <Field
            label="Subreddits to monitor"
            optional
            help="Optional. If empty, we'll suggest some after analysis."
          >
            <TagInput
              values={form.subreddits}
              onChange={set("subreddits")}
              placeholder="e.g. realtors"
              prefix="r/"
              parse={parseSubredditName}
            />
          </Field>

          {apiError && (
            <div className="field-error-text" style={{ marginBottom: 10 }}>
              {apiError}
            </div>
          )}

          <div className="onboard-actions">
            <span className="small-note">
              <window.I.ShieldCheck size={12} style={{ verticalAlign: "-2px", color: "var(--success)" }} />
              {" "}Read-only. We never post, comment, DM, or vote.
            </span>
            <div className="spacer" />
            {mode === "onboard" && (
              <button className="btn btn-secondary" onClick={onBack}>Back</button>
            )}
            <button className="btn btn-primary" onClick={submit} disabled={submitting}>
              {submitting ? (
                <>
                  <window.I.Refresh size={14} style={{ animation: "spin 900ms linear infinite" }} />
                  Analyzing…
                </>
              ) : mode === "edit" ? (
                <>Save changes</>
              ) : (
                <>Generate opportunity map <window.I.ArrowRight size={14} /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, help, error, optional, children }) {
  return (
    <div className="field">
      <label className="field-label">
        {label}
        {optional && <span className="optional">— optional</span>}
      </label>
      {help && <p className="field-help">{help}</p>}
      {children}
      {error && <div className="field-error-text">{error}</div>}
    </div>
  );
}

function TagInput({ values = [], onChange, placeholder, prefix = "", accent = false, parse = null }) {
  const [draft, setDraft] = React.useState("");
  const inputRef = React.useRef(null);

  const add = (v) => {
    const t = parse ? parse(v) : v.trim();
    if (!t) return;
    if (values.includes(t)) return;
    onChange([...values, t]);
    setDraft("");
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && !draft && values.length) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="tag-input" onClick={() => inputRef.current?.focus()}>
      {values.map((v) => (
        <span key={v} className={"tag " + (accent ? "tag-accent" : "")}>
          {prefix}{v}
          <span className="x" onClick={(e) => { e.stopPropagation(); onChange(values.filter((x) => x !== v)); }}>
            <window.I.X size={11} />
          </span>
        </span>
      ))}
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={() => draft && add(draft)}
        placeholder={values.length ? "" : placeholder}
      />
    </div>
  );
}

window.ProfileSetupScreen = ProfileSetupScreen;
